<template>
  <v-container fluid class="intelligence-interview-prep pa-4">
    <v-row>
      <v-col cols="12">
        <h1 class="text-h5 font-weight-bold mb-1 theme-heading">
          Interview Prep
        </h1>
        <p class="text-subtitle-2 grey--text text--darken-1 mb-4">
          Generate tailored STAR-validation interview questions for any
          candidate
        </p>
      </v-col>
    </v-row>

    <v-alert v-if="hasError" type="error" dismissible class="mb-4">
      {{ errorMessage }}
    </v-alert>

    <v-row>
      <v-col cols="12" md="5">
        <v-card outlined class="pa-4">
          <v-autocomplete
            v-model="selectedProfile"
            :items="autocompleteResults"
            :loading="isSearching"
            :search-input.sync="searchText"
            item-text="name"
            item-value="profileId"
            return-object
            label="Search for a candidate..."
            outlined
            dense
            hide-no-data
            color="#007AFF"
            class="mb-3"
            prepend-inner-icon="mdi-account-search"
            @focus="searchProfiles('')"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle
                  >{{ item.currentTitle || item.title
                  }}{{
                    item.currentCompany ? " at " + item.currentCompany : ""
                  }}</v-list-item-subtitle
                >
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <div
            v-if="profileName"
            class="mb-3 pa-2"
            style="background: #E3F2FD; border-radius: 4px"
          >
            <v-icon small color="#007AFF" class="mr-1"
              >mdi-account-check</v-icon
            >
            <span class="text-body-2 font-weight-medium theme-heading">{{
              profileName
            }}</span>
            <div
              v-if="profileRole || profileDepartment"
              class="text-caption grey--text text--darken-1 ml-6"
            >
              {{ profileRole }}{{ profileRole && profileDepartment ? " · " : ""
              }}{{ profileDepartment }}
            </div>
          </div>

          <v-select
            v-model="maxQuestions"
            :items="questionCounts"
            label="Number of questions"
            outlined
            dense
            color="#007AFF"
            class="mb-3"
          />

          <v-combobox
            v-model="focusSkills"
            label="Focus skills (optional)"
            outlined
            dense
            multiple
            small-chips
            deletable-chips
            hide-details
            color="#007AFF"
            class="mb-3"
          />

          <v-switch
            v-model="showJD"
            label="Include job description"
            color="#007AFF"
            dense
            hide-details
            class="mb-3"
          />

          <v-textarea
            v-if="showJD"
            v-model="jobDescription"
            label="Paste job description to tailor questions..."
            outlined
            rows="6"
            color="#007AFF"
            hide-details
            class="mb-4"
          />

          <v-btn
            block
            color="#007AFF"
            dark
            :loading="isLoading"
            :disabled="!profileId"
            @click="generate"
          >
            <v-icon left>mdi-head-question</v-icon>
            Generate Questions
          </v-btn>
        </v-card>
      </v-col>

      <v-col cols="12" md="7">
        <div v-if="!hasQuestions && !isLoading">
          <v-card outlined class="pa-6 text-center">
            <v-icon size="64" color="#E0E0E0" class="mb-3"
              >mdi-comment-question-outline</v-icon
            >
            <h3 class="text-subtitle-1 font-weight-bold mb-2 theme-heading">
              How Interview Prep Works
            </h3>
            <div
              class="text-body-2 grey--text text--darken-1 mb-4"
              style="max-width: 420px; margin: 0 auto"
            >
              Select a candidate and our AI will analyze their STAR
              achievements, skills, and career evidence to generate targeted
              behavioral interview questions that probe the depth of their
              claims.
            </div>
            <v-row dense class="justify-center">
              <v-col
                cols="auto"
                v-for="feature in features"
                :key="feature.label"
              >
                <v-chip small outlined color="#007AFF" class="ma-1">
                  <v-icon x-small left>{{ feature.icon }}</v-icon>
                  {{ feature.label }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card>
        </div>

        <div v-if="hasQuestions">
          <div class="d-flex align-center justify-space-between mb-3">
            <div>
              <span class="text-subtitle-2 font-weight-bold theme-heading">
                {{ questions.length }} Questions for {{ profileName }}
              </span>
              <v-chip
                x-small
                color="#007AFF"
                text-color="white"
                class="ml-2"
                v-if="isJdBased"
              >
                JD-Tailored
              </v-chip>
            </div>
            <v-btn small text color="#007AFF" @click="copyAll">
              <v-icon small left>mdi-content-copy</v-icon>
              Copy All
            </v-btn>
          </div>

          <v-card
            v-for="(q, idx) in questions"
            :key="idx"
            outlined
            class="question-card mb-3"
          >
            <v-card-text class="py-3 px-4">
              <div class="d-flex align-start">
                <div class="question-number mr-3">{{ idx + 1 }}</div>
                <div class="flex-grow-1">
                  <div
                    class="text-body-2 font-weight-medium mb-2 theme-heading"
                  >
                    {{ q.question }}
                  </div>
                  <div class="d-flex align-center">
                    <v-chip
                      x-small
                      :color="categoryColor(q.category)"
                      text-color="white"
                      class="mr-2"
                    >
                      {{ q.category }}
                    </v-chip>
                    <span
                      class="text-caption grey--text text--darken-1"
                      v-if="q.rationale"
                    >
                      {{ q.rationale }}
                    </span>
                  </div>
                  <div
                    v-if="q.expectedEvidence && q.expectedEvidence.length"
                    class="mt-2"
                  >
                    <v-btn
                      x-small
                      text
                      color="#007AFF"
                      class="px-1"
                      @click="toggleEvidence(idx)"
                    >
                      <v-icon x-small left>{{
                        expandedEvidence[idx]
                          ? "mdi-chevron-up"
                          : "mdi-chevron-down"
                      }}</v-icon>
                      Expected Evidence ({{ q.expectedEvidence.length }})
                    </v-btn>
                    <v-expand-transition>
                      <div v-if="expandedEvidence[idx]" class="mt-2">
                        <div
                          v-for="(ev, evIdx) in q.expectedEvidence"
                          :key="evIdx"
                          class="evidence-item pa-3 mb-2"
                        >
                          <div
                            class="text-body-2 font-weight-medium mb-2 theme-heading"
                          >
                            {{ ev.achievement || ev.title }}
                          </div>
                          <div class="d-flex flex-wrap" style="gap: 6px">
                            <v-chip
                              v-if="ev.situation"
                              x-small
                              outlined
                              color="#007AFF"
                              class="star-chip"
                            >
                              <span class="font-weight-bold mr-1">S</span>
                              {{ ev.situation }}
                            </v-chip>
                            <v-chip
                              v-if="ev.task"
                              x-small
                              outlined
                              color="#F59E0B"
                              class="star-chip"
                            >
                              <span class="font-weight-bold mr-1">T</span>
                              {{ ev.task }}
                            </v-chip>
                            <v-chip
                              v-if="ev.action"
                              x-small
                              outlined
                              color="#10B981"
                              class="star-chip"
                            >
                              <span class="font-weight-bold mr-1">A</span>
                              {{ ev.action }}
                            </v-chip>
                            <v-chip
                              v-if="ev.result"
                              x-small
                              outlined
                              color="#8B5CF6"
                              class="star-chip"
                            >
                              <span class="font-weight-bold mr-1">R</span>
                              {{ ev.result }}
                            </v-chip>
                          </div>
                        </div>
                      </div>
                    </v-expand-transition>
                  </div>
                </div>
                <v-btn icon x-small @click="copyQuestion(q)" class="ml-2">
                  <v-icon small>mdi-content-copy</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <v-progress-linear v-if="isLoading" indeterminate color="#007AFF" />
      </v-col>
    </v-row>

    <v-snackbar v-model="showCopied" :timeout="2000" color="#10B981">
      Copied to clipboard
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  name: "IntelligenceInterviewPrep",
  data() {
    return {
      searchText: "",
      showJD: false,
      showCopied: false,
      expandedEvidence: {},
      questionCounts: [3, 5, 8, 10],
      features: [
        { label: "STAR Validation", icon: "mdi-star-four-points" },
        { label: "Behavioral Questions", icon: "mdi-account-question" },
        { label: "Evidence-Based", icon: "mdi-shield-check" },
        { label: "JD-Tailored", icon: "mdi-file-document-check" },
        { label: "Compliance Safe", icon: "mdi-lock-check" },
      ],
    };
  },
  computed: {
    ...mapState("intelligenceInterviewPrep", [
      "profileId",
      "profileName",
      "profileRole",
      "profileDepartment",
      "questions",
      "isJdBased",
      "autocompleteResults",
      "isLoading",
      "isSearching",
      "hasError",
      "errorMessage",
    ]),
    ...mapGetters("intelligenceInterviewPrep", ["hasQuestions"]),
    selectedProfile: {
      get() {
        return this.profileId
          ? { profileId: this.profileId, name: this.profileName }
          : null;
      },
      set(val) {
        if (val)
          this.$store.commit("intelligenceInterviewPrep/selectProfile", val);
      },
    },
    jobDescription: {
      get() {
        return this.$store.state.intelligenceInterviewPrep.jobDescription;
      },
      set(val) {
        this.$store.commit("intelligenceInterviewPrep/updateField", {
          path: "jobDescription",
          value: val,
        });
      },
    },
    focusSkills: {
      get() {
        return this.$store.state.intelligenceInterviewPrep.focusSkills;
      },
      set(val) {
        this.$store.commit("intelligenceInterviewPrep/updateField", {
          path: "focusSkills",
          value: val,
        });
      },
    },
    maxQuestions: {
      get() {
        return this.$store.state.intelligenceInterviewPrep.maxQuestions;
      },
      set(val) {
        this.$store.commit("intelligenceInterviewPrep/updateField", {
          path: "maxQuestions",
          value: val,
        });
      },
    },
  },
  watch: {
    searchText(val) {
      this.searchProfiles(val || "");
    },
  },
  async mounted() {
    const profileId = this.$route.query.profileId;
    const queryName = this.$route.query.name;
    const queryRole = this.$route.query.role;
    const queryDepartment = this.$route.query.department;
    if (profileId) {
      this.$store.commit("intelligenceInterviewPrep/updateField", {
        path: "profileId",
        value: profileId,
      });
      if (queryName) {
        this.$store.commit("intelligenceInterviewPrep/updateField", {
          path: "profileName",
          value: queryName,
        });
      }
      if (queryRole) {
        this.$store.commit("intelligenceInterviewPrep/updateField", {
          path: "profileRole",
          value: queryRole,
        });
      }
      if (queryDepartment) {
        this.$store.commit("intelligenceInterviewPrep/updateField", {
          path: "profileDepartment",
          value: queryDepartment,
        });
      }
      await this.searchProfiles(queryName || profileId);
      const match = this.autocompleteResults.find(
        (p) => p.profileId === profileId || p.name === profileId,
      );
      if (match) {
        this.$store.commit("intelligenceInterviewPrep/selectProfile", match);
      } else if (queryName) {
        this.$store.commit("intelligenceInterviewPrep/selectProfile", {
          profileId: profileId,
          name: queryName,
        });
      } else if (this.autocompleteResults.length > 0) {
        this.$store.commit(
          "intelligenceInterviewPrep/selectProfile",
          this.autocompleteResults[0],
        );
      }
    }
  },
  methods: {
    ...mapActions("intelligenceInterviewPrep", [
      "generateQuestions",
      "searchProfiles",
    ]),
    generate() {
      this.generateQuestions();
    },
    categoryColor(cat) {
      const colors = {
        technical: "#007AFF",
        behavioral: "#10B981",
        situational: "#F59E0B",
        leadership: "#8B5CF6",
      };
      return colors[(cat || "").toLowerCase()] || "#9E9E9E";
    },
    copyQuestion(q) {
      navigator.clipboard.writeText(q.question);
      this.showCopied = true;
    },
    toggleEvidence(idx) {
      this.$set(this.expandedEvidence, idx, !this.expandedEvidence[idx]);
    },
    copyAll() {
      const text = this.questions
        .map((q, i) => `${i + 1}. [${q.category}] ${q.question}`)
        .join("\n\n");
      navigator.clipboard.writeText(text);
      this.showCopied = true;
    },
  },
};
</script>

<style scoped>
.intelligence-interview-prep {
  max-width: 1400px;
}
.question-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #007aff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
  margin-top: 2px;
}
.question-card {
  transition: box-shadow 0.2s ease;
}
.question-card:hover {
  box-shadow: 0 2px 8px rgba(30, 58, 95, 0.15);
}
.evidence-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  border-left: 3px solid #007aff;
}
.star-chip {
  max-width: 100%;
  height: auto !important;
  white-space: normal;
  padding: 4px 8px;
}
.star-chip .v-chip__content {
  white-space: normal;
  line-height: 1.3;
}
</style>
